import React, { Component } from "react";
require("../styles/radioFeed.less");
import { connect, ConnectedProps } from "react-redux";
import {
  pauseRadio,
  playRadio,
  RADIO_FEED_STATE,
  RADIO_STATE,
  setRadioFeedState,
  setVolume,
} from "../store/liveRadio/action";
import { RootState } from "../store/store";
import { RadioMetadata, Song, ValueOf } from "../store/liveRadio/reducer";
import { Spin, Slider } from "antd";
import {
  CaretRightOutlined,
  LeftOutlined,
  PauseOutlined,
  RightOutlined,
} from "@ant-design/icons";
import SmartRollText from "./SmartRollText";
import { SizeMe } from "react-sizeme";
import LogoAudioVisualizer from "./LogoAudioVisualizer";

import _throttle from "lodash/throttle";

const mapState = (state: RootState) => ({
  playing: state.liveRadio.state === RADIO_STATE.PLAYING,
  radioMetadata: state.liveRadio.metadata,
  radioFeedState: state.liveRadio.radioFeedState,
  volume: state.liveRadio.volume,
});

const mapDispatch = {
  playRadio,
  pauseRadio,
  setRadioFeedState,
  setVolume,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type RadioFeedProps = PropsFromRedux & {
  playing: boolean;
  radioMetadata: RadioMetadata;
  radioFeedState: ValueOf<typeof RADIO_FEED_STATE>;
  volume: number;
};
class RadioFeed extends Component<
  RadioFeedProps,
  { progress: number; hasError: boolean }
> {
  updateProgressInterval = null;

  boundTipFormatter = this.tipFormatter.bind(this);
  boundToggleMinimal = this.toggleMinimal.bind(this);

  state = {
    progress: 0,
    hasError: false,
  };

  componentDidMount() {
    if (this.updateProgressInterval == null) {
      this.updateProgress();
      this.updateProgressInterval = setInterval(
        this.updateProgress.bind(this),
        500
      );
    }
  }

  updateProgress() {
    const currentSong = this.getCurrentSong();

    if (currentSong == null) {
      // cheeky - could update current time? hmmm idk what the best way to do this is?
      this.forceUpdate();
      return;
    }
    this.setState({
      progress: +(
        100 *
        ((+new Date() - currentSong.cued_at * 1000) /
          (currentSong.duration * 1000))
      ).toFixed(2),
    });
  }

  componentWillUnmount() {
    if (this.updateProgressInterval != null) {
      clearInterval(this.updateProgressInterval);
    }
  }

  toggleMinimal() {
    if (this.props.radioFeedState === RADIO_FEED_STATE.MINIMAL) {
      this.props.setRadioFeedState(RADIO_FEED_STATE.SHOWN);
    } else if (this.props.radioFeedState === RADIO_FEED_STATE.SHOWN) {
      this.props.setRadioFeedState(RADIO_FEED_STATE.MINIMAL);
    }
  }

  getCurrentSong(): Song {
    const radioMetadata = this.props.radioMetadata;

    const enqueuedSongs = radioMetadata?.enqueuedSongs;

    if (enqueuedSongs == null) {
      return null;
    }

    const curDate = +new Date() / 1000;

    for (const song of enqueuedSongs) {
      if (curDate >= song.cued_at && curDate <= song.cued_at + song.duration) {
        return song;
      }
    }

    return null;
  }

  tipFormatter(value: number) {
    return value.toFixed(0) + "%";
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    const defaultSong: Song = {
      duration: 0,
      title: "Purple Radio",
      artist: "The Team",
      cued_at: 0,
      art: null,
      album: null,
      playlist: null,
      text: "Purple Radio - The Team",
    };
    const currentSong: Song = this.getCurrentSong() || defaultSong;

    return (
      <div
        className={`radio-feed ${
          this.props?.radioFeedState !== RADIO_FEED_STATE.HIDDEN
            ? "playing"
            : ""
        } ${this.props?.radioMetadata?.audioURL ? "" : "loading"} ${
          this.props?.radioFeedState === RADIO_FEED_STATE.MINIMAL
            ? "minimal"
            : ""
        }`}
      >
        <Spin
          spinning={!this?.props?.radioMetadata?.audioURL}
          className={"spin-class"}
          wrapperClassName={"spin-class"}
        >
          <div
            className="to-minimal desktop-only"
            onClick={this.boundToggleMinimal}
          >
            {this.props.radioFeedState === RADIO_FEED_STATE.MINIMAL ? (
              <LeftOutlined />
            ) : (
              <RightOutlined />
            )}
          </div>
          {this.props?.radioMetadata?.audioURL ? (
            <>
              <SizeMe>
                {({ size }) => (
                  <div className="left-item">
                    <div className="track">
                      <SmartRollText maxWidth={size.width}>
                        <h3>{currentSong.title || defaultSong.title} </h3>
                      </SmartRollText>
                      <SmartRollText maxWidth={size.width}>
                        <h4>{currentSong.artist || defaultSong.artist} </h4>
                      </SmartRollText>
                    </div>
                    <div
                      className={"controls"}
                      onClick={
                        this.props.playing
                          ? this.props.pauseRadio
                          : this.props.playRadio
                      }
                    >
                      {this.props.playing ? (
                        <PauseOutlined />
                      ) : (
                        <CaretRightOutlined />
                      )}
                    </div>
                    <div className="slide-container">
                      <Slider
                        defaultValue={this.props.volume * 100}
                        onChange={_throttle(
                          (newVol: number) =>
                            // clamp to between 0 and 1
                            this.props.setVolume(
                              Math.min(1, Math.max(0, newVol / 100))
                            ),
                          100
                        )}
                        tipFormatter={this.boundTipFormatter}
                      />
                    </div>
                  </div>
                )}
              </SizeMe>
              <div className="album-art">
                {currentSong.art ? (
                  <>
                    <div
                      className={"minimal-controls"}
                      onClick={
                        this.props.playing
                          ? this.props.pauseRadio
                          : this.props.playRadio
                      }
                    >
                      <span className="minimal-controls-helper"></span>
                      {this.props.playing ? (
                        <PauseOutlined />
                      ) : (
                        <CaretRightOutlined />
                      )}
                    </div>
                    <img
                      src={currentSong.art}
                      onClick={
                        this.props.playing
                          ? this.props.pauseRadio
                          : this.props.playRadio
                      }
                    />
                  </>
                ) : (
                  <LogoAudioVisualizer
                    className="logo-viz-radio"
                    onClick={
                      this.props.playing
                        ? this.props.pauseRadio
                        : this.props.playRadio
                    }
                  />
                )}
              </div>
            </>
          ) : null}
        </Spin>
      </div>
    );
  }
}

export default connector(RadioFeed);
